import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search, Star, Code2, FileDown, Sparkles, ShieldQuestion, Flag, ChevronDown, ArrowRight,
  Smartphone, Apple, Check, Heart, Tag, Music2,
} from "lucide-react";
import { FoinnLogo } from "../components/FoinnLogo";
import { useAuth } from "../contexts/AuthContext";

const TITLE = "Foinn.org · Folk Tune Finder — find, play and print traditional tunes";
const DESCRIPTION =
  "Foinn.org is a free folk tune finder for 23,000+ traditional Irish, Scottish and European tunes: " +
  "search by name, type, key or fame, hear them played, edit the ABC, print clean A4 sheet music " +
  "and ask an AI expert about a tune's history.";

const FEATURES = [
  { icon: Search, title: "Search by name, notes (ABC), type, key — or by fame", text: "Slide from session standards to tunes almost nobody plays. Every search reshuffles, so there is always something new." },
  { icon: Music2, title: "Hear it before you learn it", text: "Built-in player with tempo control, follow-the-notes highlighting and full-screen notation." },
  { icon: Star, title: "Favourites that follow you", text: "Star any tune — signed in, your favourites sync across phone, tablet and laptop." },
  { icon: Flag, title: "Colour flags for your practice list", text: "Tap the flag to cycle your colours. Rename each one (\u201cTo learn\u201d, \u201cIn my repertoire\u201d), filter the flags page by colour \u2014 and premium members can create up to 50 colours." },
  { icon: Code2, title: "Edit and transpose the ABC", text: "Change a setting, transpose to your instrument, keep your own version — your line breaks are respected." },
  { icon: FileDown, title: "Print-perfect A4 sheet music", text: "Vector PDF export with live preview, portrait or landscape, a zoom that snaps to the tune's own line breaks and a Fit button that fills the page." },
  { icon: Sparkles, title: "Ask the AI expert", text: "Composer, era, region, first recording and the story of the tune — enriched with thesession.org comments. Already-studied tunes are free for everyone." },
  { icon: ShieldQuestion, title: "Verify rarity", text: "Check whether a rare find shares its name with a session standard, and vote if you think its ranking is off." },
];

const PLANS = [
  { title: "Guest", price: "Free", rows: ["5 searches / day", "No AI analyses", "Notation, player & PDF", "Favourites on this device"], accent: "#a89e92" },
  { title: "Signed in", price: "Free", rows: ["20 searches / day", "3 welcome AI analyses", "Everything in Guest", "Favourites synced"], accent: "#2d2824" },
  { title: "Premium", price: "€2 / month", rows: ["Unlimited searches", "10 AI analyses / month", "Manual data refresh", "Cancel in two clicks"], accent: "#8a2be2", highlight: true },
  { title: "1 year pass", price: "€20 / year", rows: ["Unlimited searches", "15 AI analyses / month", "Manual data refresh", "Two months free"], accent: "#0f8a5f" },
];

const PACKS = [
  { id: "calls_50", price: "€5", title: "AI pack · 50", sub: "50 expert analyses + 50 extra searches", accent: "#8a2be2" },
  { id: "calls_120", price: "€10", title: "AI pack · 120", sub: "120 expert analyses + 120 extra searches", accent: "#0f8a5f" },
];

const FAQ = [
  {
    q: "Where do the tunes come from?",
    a: "From the official thesession.org data dumps — more than 23,000 tunes and 55,000 settings, refreshed monthly. Foinn.org adds search, playback, printing and its own rarity ranking on top.",
  },
  {
    q: "What does the fame ranking actually mean?",
    a: "It is an absolute notability score: recordings, sets, tunebooks, settings and aliases. One-off member compositions sit at 0–15%, widely circulated tunes around 30–60%, and session standards like Cooley's reach 90–100%. You can also vote if you think a tune deserves better (or worse).",
  },
  {
    q: "Do I need an account?",
    a: "No — you can search as a guest. Signing in with Google gives you 20 searches a day, three free AI analyses and favourites that follow you across devices.",
  },
  {
    q: "What happens to the money from Premium and donations?",
    a: "It keeps Foinn.org alive and independent: the domain and publication costs, hosting and the database, the AI service that writes the expert cards, and the time to build new features. There is no advertising and no data selling — that is the whole point.",
  },
];

const Accordion = ({ id, icon: Icon, title, open, onToggle, children }) => (
  <section className="overflow-hidden rounded-3xl border border-[#e5e0d8] bg-white" data-testid={`guide-${id}`}>
    <button
      onClick={onToggle}
      className="flex w-full items-center gap-3 px-5 py-4 text-left"
      data-testid={`guide-toggle-${id}`}
      aria-expanded={open}
    >
      <Icon className="h-5 w-5 shrink-0 text-[#8a2be2]" />
      <h2 className="flex-1 font-serif text-2xl font-bold text-[#2d2824]">{title}</h2>
      <ChevronDown className={`h-5 w-5 shrink-0 text-[#a89e92] transition-transform ${open ? "rotate-180" : ""}`} />
    </button>
    {open && <div className="border-t border-[#f3efea] px-5 py-5">{children}</div>}
  </section>
);

export default function AboutPage() {
  const navigate = useNavigate();
  const { user, openPricing } = useAuth();
  const [open, setOpen] = useState("features");

  useEffect(() => {
    document.title = TITLE;
    const set = (sel, attr, val) => {
      let el = document.head.querySelector(sel);
      if (!el) {
        el = document.createElement(sel.startsWith("link") ? "link" : "meta");
        if (sel.includes("name=")) el.setAttribute("name", sel.split('"')[1]);
        if (sel.includes("property=")) el.setAttribute("property", sel.split('"')[1]);
        if (sel.startsWith("link")) el.setAttribute("rel", "canonical");
        document.head.appendChild(el);
      }
      el.setAttribute(attr, val);
    };
    set('meta[name="description"]', "content", DESCRIPTION);
    set('meta[property="og:title"]', "content", TITLE);
    set('meta[property="og:description"]', "content", DESCRIPTION);
    set('link[rel="canonical"]', "href", `${window.location.origin}/about`);

    const ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.text = JSON.stringify({
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebApplication",
          name: "Foinn.org · Folk Tune Finder",
          url: window.location.origin,
          applicationCategory: "MusicApplication",
          operatingSystem: "Any",
          description: DESCRIPTION,
          offers: [
            { "@type": "Offer", price: "0", priceCurrency: "EUR", name: "Free" },
            { "@type": "Offer", price: "2", priceCurrency: "EUR", name: "Premium monthly" },
            { "@type": "Offer", price: "20", priceCurrency: "EUR", name: "One year pass" },
          ],
        },
        {
          "@type": "FAQPage",
          mainEntity: FAQ.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        },
      ],
    });
    document.head.appendChild(ld);
    return () => {
      document.head.removeChild(ld);
      document.title = "Foinn.org · Folk Tune Finder";
    };
  }, []);

  const toggle = (id) => setOpen((o) => (o === id ? null : id));

  return (
    <div className="min-h-screen bg-[#f7f3ec]" data-testid="about-page">
      {/* hero */}
      <header className="mx-auto max-w-5xl px-5 pt-8">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <FoinnLogo className="h-14 w-14" />
            <div>
              <p className="font-serif text-3xl font-bold leading-none text-[#2d2824]">Foinn.org</p>
              <p className="text-[11px] font-bold uppercase tracking-widest text-[#a89e92]">Folk Tune Finder</p>
            </div>
          </div>
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 rounded-full bg-[#2d2824] px-5 py-3 text-sm font-extrabold text-white transition-transform hover:scale-105"
            data-testid="about-open-app"
          >
            Open Foinn.org <ArrowRight className="h-4 w-4" />
          </button>
        </div>

        <div className="mt-8 max-w-2xl">
            <h1 className="font-serif text-4xl font-bold leading-[1.05] text-[#2d2824] sm:text-5xl lg:text-6xl">
              23,000 traditional tunes.
              <span className="block text-[#8a2be2]">Find the one you half remember.</span>
            </h1>
            <p className="mt-4 max-w-xl text-base text-[#6e655b]">
              Search by name, by a few notes in ABC, by type or key — or by how famous a tune is, from session standards to
              tunes nobody plays any more. Hear it, edit it, flag it, print it, and ask an AI
              musicologist where it comes from. Free to use, no advertising.
            </p>
            <div className="mt-5 flex flex-wrap gap-2">
              <button
                onClick={() => navigate("/")}
                className="flex items-center gap-2 rounded-full bg-[#2d2824] px-6 py-3.5 text-base font-extrabold text-white transition-transform hover:scale-105"
                data-testid="about-cta-search"
              >
                <Search className="h-5 w-5" /> Start searching
              </button>
              <button
                onClick={() => navigate("/signin")}
                className="rounded-full border-2 border-[#2d2824] px-6 py-3.5 text-base font-extrabold text-[#2d2824] transition-transform hover:scale-105"
                data-testid="about-cta-signin"
              >
                Sign in — it's free
              </button>
            </div>
        </div>
      </header>

      <main className="mx-auto mt-10 max-w-5xl space-y-4 px-5 pb-16">
        <Accordion id="features" icon={Music2} title="What Foinn.org does" open={open === "features"} onToggle={() => toggle("features")}>
          <div className="grid gap-4 sm:grid-cols-2">
            {FEATURES.map((f) => (
              <div key={f.title} className="flex gap-3">
                <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#f3efea]">
                  <f.icon className="h-5 w-5 text-[#8a2be2]" />
                </span>
                <div>
                  <h3 className="text-base font-extrabold text-[#2d2824]">{f.title}</h3>
                  <p className="text-sm text-[#6e655b]">{f.text}</p>
                </div>
              </div>
            ))}
          </div>
        </Accordion>

        <Accordion id="pricing" icon={Tag} title="Plans & pricing" open={open === "pricing"} onToggle={() => toggle("pricing")}>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {PLANS.map((p) => (
              <div
                key={p.title}
                className="rounded-2xl border-2 bg-[#fffdf9] p-4"
                style={{ borderColor: p.highlight ? p.accent : "#e5e0d8" }}
              >
                <p className="font-serif text-2xl font-bold text-[#2d2824]">{p.title}</p>
                <p className="text-lg font-black" style={{ color: p.accent }}>{p.price}</p>
                <ul className="mt-2 space-y-1 text-sm text-[#6e655b]">
                  {p.rows.map((r) => (
                    <li key={r} className="flex items-start gap-1.5">
                      <Check className="mt-0.5 h-3.5 w-3.5 shrink-0" style={{ color: p.accent }} />
                      {r}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="mt-4">
            <h3 className="mb-2 text-[11px] font-extrabold uppercase tracking-widest text-[#6e655b]">
              Add-on packs · one-off, never expire
            </h3>
            <div className="grid gap-3 sm:grid-cols-2">
              {PACKS.map((p) => (
                <div
                  key={p.id}
                  className="flex items-center justify-between gap-3 rounded-2xl border-2 border-[#e5e0d8] bg-[#fffdf9] p-4"
                  data-testid={`about-pack-${p.id}`}
                >
                  <div className="min-w-0">
                    <p className="font-serif text-xl font-bold text-[#2d2824]">{p.title}</p>
                    <p className="text-xs font-bold text-[#6e655b]">{p.sub}</p>
                  </div>
                  <button
                    onClick={() => (user ? openPricing() : navigate("/signin"))}
                    className="shrink-0 rounded-full px-5 py-2.5 text-sm font-extrabold text-white transition-transform hover:scale-105"
                    style={{ background: p.accent }}
                    data-testid={`about-pack-buy-${p.id}`}
                  >
                    {p.price}
                  </button>
                </div>
              ))}
            </div>
          </div>
          <p className="mt-3 text-sm text-[#6e655b]">
            Cached analyses are always free, whatever your plan — you only spend an analysis when a
            tune has never been studied before.
          </p>
        </Accordion>

        <Accordion id="install" icon={Smartphone} title="Put Foinn.org on your phone" open={open === "install"} onToggle={() => toggle("install")}>
          <p className="mb-4 text-sm text-[#6e655b]">
            Foinn.org is a web app — no store, no download. Add it to your home screen and it opens
            full-screen with its own icon, like any other app.
          </p>
          <div className="grid gap-5 sm:grid-cols-2">
            <div>
              <h3 className="flex items-center gap-2 text-base font-extrabold text-[#2d2824]">
                <Apple className="h-5 w-5" /> iPhone & iPad (Safari)
              </h3>
              <img src="/install-ios.jpeg" alt="Safari share menu on an iPhone" className="my-3 w-full rounded-2xl border border-[#e5e0d8]" loading="lazy" />
              <ol className="ml-4 list-decimal space-y-1 text-sm text-[#6e655b]">
                <li>Open Foinn.org in <b>Safari</b> (it must be Safari, not Chrome).</li>
                <li>Tap the <b>Share</b> icon in the bottom bar.</li>
                <li>Scroll down and tap <b>Add to Home Screen</b>.</li>
                <li>Confirm with <b>Add</b> — the Foinn.org logo appears on your home screen.</li>
              </ol>
            </div>
            <div>
              <h3 className="flex items-center gap-2 text-base font-extrabold text-[#2d2824]">
                <Smartphone className="h-5 w-5" /> Android (Chrome)
              </h3>
              <img src="/install-android.jpeg" alt="Chrome menu on an Android phone" className="my-3 w-full rounded-2xl border border-[#e5e0d8]" loading="lazy" />
              <ol className="ml-4 list-decimal space-y-1 text-sm text-[#6e655b]">
                <li>Open Foinn.org in <b>Chrome</b>.</li>
                <li>Tap the <b>⋮</b> menu, top right.</li>
                <li>Choose <b>Install app</b> (or <b>Add to Home screen</b>).</li>
                <li>Confirm — Foinn.org installs with its own icon and opens full-screen.</li>
              </ol>
            </div>
          </div>
        </Accordion>

        {/* FAQ */}
        <section className="rounded-3xl border border-[#e5e0d8] bg-white p-5" data-testid="about-faq">
          <h2 className="font-serif text-3xl font-bold text-[#2d2824]">Questions</h2>
          <div className="mt-4 space-y-4">
            {FAQ.map((f) => (
              <div key={f.q}>
                <h3 className="text-base font-extrabold text-[#2d2824]">{f.q}</h3>
                <p className="mt-1 text-sm leading-relaxed text-[#6e655b]">{f.a}</p>
              </div>
            ))}
          </div>
          <div className="mt-5 flex items-start gap-2 rounded-2xl bg-[#fff1f3] p-4 text-sm text-[#6e655b]">
            <Heart className="mt-0.5 h-4 w-4 shrink-0 text-[#e11d48]" />
            <span>
              <b className="text-[#2d2824]">Why donate?</b> Every €5, €10 or €20 goes into keeping
              Foinn.org online and improving it: domain and publication costs, hosting, the tune
              database and the AI service — nothing else.
            </span>
          </div>
        </section>

        <div className="flex flex-wrap items-center justify-between gap-3 rounded-3xl bg-[#2d2824] p-6">
          <p className="font-serif text-2xl font-bold text-white">Ready to find your tune?</p>
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 rounded-full bg-white px-6 py-3 text-base font-extrabold text-[#2d2824] transition-transform hover:scale-105"
            data-testid="about-footer-cta"
          >
            Open Foinn.org <ArrowRight className="h-4 w-4" />
          </button>
        </div>

        <footer className="flex flex-wrap items-center justify-center gap-3 pt-2 text-xs text-[#6e655b]">
          <a href="https://thesession.org" target="_blank" rel="noreferrer" className="font-bold underline">
            Tune data: thesession.org
          </a>
          <button onClick={() => navigate("/privacy")} className="font-bold underline" data-testid="about-privacy-link">
            Privacy & GDPR
          </button>
        </footer>
      </main>
    </div>
  );
}
